/**
 * 工作流类型定义
 *
 * 基于 React Flow v12+ 定义工作流画布相关的 TypeScript 类型
 * 参考: docs/ragflow_web 工作流设计
 */

import type { Node, Edge } from '@xyflow/react';
import type { ToolName } from './agent';

// ==================== 执行状态枚举 ====================

/** 执行状态枚举 */
export enum ExecutionState {
  IDLE = 'idle',
  RUNNING = 'running',
  PAUSED = 'paused',
  STEP_WAITING = 'step_waiting',
  COMPLETED = 'completed',
  FAILED = 'failed',
  INTERRUPTED = 'interrupted',
}

// ==================== 节点状态枚举 ====================

/** 节点执行状态 */
export enum NodeExecutionStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

// ==================== 工作流上下文 ====================

/** 循环上下文 */
export interface LoopContext {
  iteration_index: number;
  total_iterations: number;
  chapter_start: number;
  chapter_end: number;
  current_chapter: number;
}

/** 工作区上下文 */
export interface WorkspaceContext {
  workspace_id: number;
  workspace_name: string;
  total_chapters: number;
  genre: string;
}

/** 信息存储（临时内存） */
export interface InfoStore {
  [nodeName: string]: Record<string, unknown>;
}

/** 工作流执行上下文 */
export interface WorkflowContext {
  task_prompt: string;
  workspace: WorkspaceContext | null;
  loop: LoopContext | null;
  info: InfoStore;
}

// ==================== 节点数据类型 ====================

/** 基础节点数据 */
export interface BaseNodeData extends Record<string, unknown> {
  label: string;
  type: string;
  description?: string;
}

/** 开始节点数据 */
export interface StartNodeData extends BaseNodeData {
  type: 'start';
  task_prompt: string;
}

/** Agent 节点数据 */
export interface AgentNodeData extends BaseNodeData {
  type: 'agent';
  agent_id: number | null;
  agent_name?: string;
  /** 使用的模型名称（如 glm-4.6, qwen3-max） */
  model?: string;
  enabled_tools: ToolName[];
  /** 允许的章节范围（从循环容器继承） */
  chapter_range?: {
    start: number;
    end: number;
  };
}

/** 循环容器节点数据 */
export interface LoopNodeData extends BaseNodeData {
  type: 'loop';
  chapter_start: number;
  chapter_end: number;
  /** 迭代步数，每次循环处理的章节数（默认 1） */
  step: number;
  /** 是否启用循环反馈 */
  loop_feedback: boolean;
  /** 子节点 ID 列表 */
  children?: string[];
}

/** 条件分支配置 */
export interface ConditionBranch {
  id: string;
  label: string;
  description: string;
  target_node_id?: string;
}

/** 条件分支节点数据 */
export interface ConditionNodeData extends BaseNodeData {
  type: 'condition';
  branches: ConditionBranch[];
  /** 默认分支 ID */
  default_branch_id?: string;
}

/** 信息提取字段定义 */
export interface InfoExtractField {
  name: string;
  description: string;
  required: boolean;
}

/** 信息节点数据 */
export interface InfoNodeData extends BaseNodeData {
  type: 'info';
  /** LLM 提取提示词 */
  prompt: string;
  /** 提取字段定义 */
  fields: InfoExtractField[];
}

/** 注释节点数据 */
export interface NoteNodeData extends BaseNodeData {
  type: 'note';
  content: string;
  /** 背景颜色 */
  color?: string;
}

/** 所有节点数据类型的联合类型 */
export type WorkflowNodeData =
  | StartNodeData
  | AgentNodeData
  | LoopNodeData
  | LoopGroupNodeData
  | ConditionNodeData
  | InfoNodeData
  | NoteNodeData;

/** 节点类型映射 */
export type WorkflowNodeType =
  | 'start'
  | 'agent'
  | 'loop'
  | 'condition'
  | 'info'
  | 'note';

// ==================== React Flow 节点和边类型 ====================

/**
 * 循环容器节点数据 (Subflow/Group)
 *
 * 使用 React Flow 的 subflows 机制实现循环容器
 * - 子节点通过 parentId 指向循环容器
 * - 子节点使用 extent: 'parent' 限制在容器内
 */
export interface LoopGroupNodeData extends BaseNodeData {
  type: 'loop';
  /** 起始章节 */
  chapter_start: number;
  /** 结束章节 */
  chapter_end: number;
  /** 迭代步数，每次循环处理的章节数 */
  step: number;
  /** 是否启用循环反馈 */
  loop_feedback: boolean;
  /** 子节点 ID 列表（用于快速查找） */
  children?: string[];
}

/** 工作流节点类型 - 使用 Record<string, unknown> 作为基础类型 */
export type WorkflowNode = Node<Record<string, unknown>, WorkflowNodeType>;

/** 边数据 */
export interface EdgeData extends Record<string, unknown> {
  /** 边标签（用于条件分支显示） */
  label?: string;
  /** 条件分支 ID */
  branch_id?: string;
  /** 执行状态 */
  executionStatus?: NodeExecutionStatus;
}

/** 工作流边类型 */
export type WorkflowEdge = Edge<EdgeData>;

// ==================== 节点日志 ====================

/** 节点日志消息 */
export interface NodeLogMessage {
  id: string;
  node_id: string;
  timestamp: string;
  type: 'info' | 'error' | 'warning' | 'success';
  message: string;
  /** 工具调用信息 */
  tool_call?: {
    tool_name: string;
    arguments: Record<string, unknown>;
    result?: unknown;
    error?: string;
  };
}

/** 节点执行日志 */
export interface NodeExecutionLog {
  node_id: string;
  node_name: string;
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  status: NodeExecutionStatus;
  messages: NodeLogMessage[];
  /** Token 消耗统计 */
  token_usage?: {
    input: number;
    output: number;
    total: number;
  };
  /** 错误信息 */
  error?: string;
}

// ==================== WebSocket 消息类型 ====================

/** WebSocket 消息类型枚举 - 与后端保持一致 */
export enum WorkflowMessageType {
  // 工作流执行事件（Task 13.1-13.4）
  WORKFLOW_EXECUTION_STARTED = 'workflow.execution.started',
  STATE_CHANGE = 'state_change',
  NODE_STARTED = 'node_started',
  NODE_PROGRESS = 'node_progress',
  NODE_FINISHED = 'node_finished',
  NODE_FAILED = 'node_failed',
  DEBUG_UPDATE = 'debug_update',
  LOOP_ITERATION = 'loop_iteration',
  // 保留旧类型以兼容
  EXECUTION_STATE_CHANGED = 'execution_state_changed',
  NODE_EXECUTION_STARTED = 'node_execution_started',
  NODE_EXECUTION_COMPLETED = 'node_execution_completed',
  NODE_EXECUTION_FAILED = 'node_execution_failed',
  LOG_MESSAGE = 'log_message',
  CONTEXT_UPDATED = 'context_updated',
}

/** 执行状态变更消息 */
export interface ExecutionStateChangedMessage {
  type: WorkflowMessageType.EXECUTION_STATE_CHANGED | WorkflowMessageType.STATE_CHANGE | string;
  payload: {
    state: ExecutionState;
    previous_state?: ExecutionState;
    current_node_id?: string;
    progress?: {
      completed: number;
      total: number;
    };
  };
}

/** 节点执行开始消息 */
export interface NodeExecutionStartedMessage {
  type: WorkflowMessageType.NODE_EXECUTION_STARTED | WorkflowMessageType.NODE_STARTED | string;
  payload: {
    node_id: string;
    node_name: string;
    node_type: WorkflowNodeType;
  };
}

/** 节点执行完成消息 */
export interface NodeExecutionCompletedMessage {
  type: WorkflowMessageType.NODE_EXECUTION_COMPLETED | WorkflowMessageType.NODE_FINISHED | string;
  payload: {
    node_id: string;
    node_name: string;
    duration_ms: number;
    token_usage?: {
      input: number;
      output: number;
      total: number;
    };
  };
}

/** 节点执行失败消息 */
export interface NodeExecutionFailedMessage {
  type: WorkflowMessageType.NODE_EXECUTION_FAILED | WorkflowMessageType.NODE_FAILED | string;
  payload: {
    node_id: string;
    node_name: string;
    error: string;
    retry_count?: number;
  };
}

/** 日志消息 */
export interface LogMessageMessage {
  type: WorkflowMessageType.LOG_MESSAGE | string;
  payload: NodeLogMessage;
}

/** 上下文更新消息 */
export interface ContextUpdatedMessage {
  type: WorkflowMessageType.CONTEXT_UPDATED | string;
  payload: {
    context: WorkflowContext;
  };
}

/** 所有 WebSocket 消息类型的联合类型 */
export type WorkflowWebSocketMessage =
  | ExecutionStateChangedMessage
  | NodeExecutionStartedMessage
  | NodeExecutionCompletedMessage
  | NodeExecutionFailedMessage
  | LogMessageMessage
  | ContextUpdatedMessage;

// ==================== 工作流定义 ====================

/** 工作流定义 */
export interface WorkflowDefinition {
  id: string;
  name: string;
  description?: string;
  workspace_id: number;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  created_at: string;
  updated_at: string;
}

/** 工作流列表项（简化版，用于列表展示） */
export interface WorkflowListItem {
  id: number;
  workspace_id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

/** 工作流创建请求 */
export interface WorkflowCreateRequest {
  name: string;
  description?: string;
  workspace_id: number;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/** 工作流更新请求 */
export interface WorkflowUpdateRequest {
  name?: string;
  description?: string;
  nodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
}

/** 工作流执行请求 */
export interface WorkflowExecuteRequest {
  workflow_id: string;
  task_prompt: string;
  debug_mode?: boolean;
}

// ==================== 工具函数 ====================

/** 获取节点类型的显示名称 */
export function getNodeTypeDisplayName(type: WorkflowNodeType): string {
  const names: Record<WorkflowNodeType, string> = {
    start: '开始节点',
    agent: 'Agent 节点',
    loop: '循环容器',
    condition: '条件分支',
    info: '信息节点',
    note: '注释',
  };
  return names[type] || type;
}

/** 获取节点执行状态的显示名称 */
export function getNodeStatusDisplayName(status: NodeExecutionStatus): string {
  const names: Record<NodeExecutionStatus, string> = {
    pending: '等待中',
    running: '执行中',
    completed: '已完成',
    failed: '失败',
    skipped: '已跳过',
  };
  return names[status] || status;
}

/** 获取节点状态的 CSS 类名 */
export function getNodeStatusClassName(status: NodeExecutionStatus): string {
  const classes: Record<NodeExecutionStatus, string> = {
    pending: 'node-status-pending',
    running: 'node-status-running',
    completed: 'node-status-completed',
    failed: 'node-status-failed',
    skipped: 'node-status-skipped',
  };
  return classes[status] || '';
}

/** 创建默认节点数据 */
export function createDefaultNodeData(type: WorkflowNodeType): WorkflowNodeData {
  switch (type) {
    case 'start':
      return {
        label: '开始',
        type: 'start',
        task_prompt: '',
      };
    case 'agent':
      return {
        label: 'Agent',
        type: 'agent',
        agent_id: null,
        model: 'glm-4.6',
        enabled_tools: [],
      };
    case 'loop':
      return {
        label: '循环容器',
        type: 'loop',
        chapter_start: 1,
        chapter_end: 10,
        step: 1,
        loop_feedback: false,
        children: [],
      };
    case 'condition':
      return {
        label: '条件分支',
        type: 'condition',
        branches: [],
      };
    case 'info':
      return {
        label: '信息提取',
        type: 'info',
        prompt: '',
        fields: [],
      };
    case 'note':
      return {
        label: '注释',
        type: 'note',
        content: '',
      };
    default:
      throw new Error(`Unknown node type: ${type}`);
  }
}

/** 验证节点数据 */
export function validateNodeData(data: WorkflowNodeData): string[] {
  const errors: string[] = [];

  switch (data.type) {
    case 'start':
      if (!data.task_prompt?.trim()) {
        errors.push('任务 prompt 不能为空');
      }
      break;
    case 'agent':
      if (!data.agent_id) {
        errors.push('请选择一个 Agent');
      }
      break;
    case 'loop':
      if (data.chapter_start > data.chapter_end) {
        errors.push('起始章节不能大于结束章节');
      }
      break;
    case 'condition':
      if (data.branches.length === 0) {
        errors.push('至少需要定义一个条件分支');
      }
      break;
    case 'info':
      if (!data.prompt?.trim()) {
        errors.push('提取提示词不能为空');
      }
      if (data.fields.length === 0) {
        errors.push('至少需要定义一个提取字段');
      }
      break;
  }

  return errors;
}
